import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Tab, Tabs, Tooltip, SvgIcon} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {Link, useParams} from "react-router-dom";
import {Col, Row} from "reactstrap";
import SummaryInfoCard from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import SummaryActionCard from "../../../../../../components/app-specific/summary-cards/summary-action-card";
import OrderInformationViewReadOnlyAddressesSection
    from "../../../../../../components/app-specific/order-inforamtion-shared-sections/read-only-addresses";
import IconButton from "../../../../../../components/base/button/icon_button";
import PurchaseVendorInfoCard from "../../shared/vendor-info-card";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {useAppSelector} from "../../../../../../redux";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import useQuery from "../../../../../../hooks/use-query";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import DateUtils from "../../../../../../../core/services/date-utils";
import Utils from "../../../../../../../core/services/utils/utils";
import {buildMockPurchaseHistory} from "../../shared/mock-documents";
import PurchaseHistorySingleProductsSection from "./sections/products";
import PurchaseHistorySingleLogsSection from "./sections/logs";
import {ReactComponent as Printer} from "../../../../../../../assets/images/printer.svg";

const tabs = Object.freeze({
    products: 'products',
    addresses: 'addresses',
    logs: 'logs',
});

const documentBodyIds = {
    vendor: 'vendor',
    vendorPO: 'vendorPO',
    reference: 'reference',
    date: 'date',
    receivedDate: 'receivedDate',
    warehouse: 'warehouse',
    currency: 'currency',
    status: 'status',
};

const totalsBodyIds = {
    subtotal: 'subtotal',
    discount: 'discount',
    tax: 'tax',
    total: 'total',
    balance: 'balance',
};

/**
 * Formats a server date value for summary display.
 */
const formatSummaryDate = (value) => {
    if (!value) {
        return '--';
    }
    return DateUtils.serverToLocal(value).format(DateUtils.dateFormat);
};

/**
 * Purchase history single — read-only document (no status actions / serial entry).
 */
const PurchaseHistorySingleView = () => {
    const {vendorId, historyId} = useParams();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.historySingle'});
    const defaultVendor = useAppSelector((state) => state.vendorInformation);
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const [query, setQuery] = useQuery();
    const [loading, setLoading] = useState(true);
    const [document, setDocument] = useState(null);
    const {configureNotebookTags} = useConfigureNotebook('vendor-purchase-history-single');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const tab = useMemo(() => {
        const requested = query?.tab;
        if (requested && Object.values(tabs).includes(requested)) {
            return requested;
        }
        return tabs.products;
    }, [query?.tab]);

    const tabsList = useMemo(() => ([
        {value: tabs.products, label: t('tabs.products')},
        {value: tabs.addresses, label: t('tabs.addresses')},
        {value: tabs.logs, label: t('tabs.logs')},
    ]), [t]);

    useEffect(() => {
        if (!defaultVendor?.code) {
            return;
        }
        configureNotebookTags((tags) => tags.vendorPurchaseHistorySingle(defaultVendor.code, historyId));
        configureBehavioralTool((tags) => tags.vendorPurchaseHistorySingle(defaultVendor.code, historyId));
    }, [configureBehavioralTool, configureNotebookTags, defaultVendor?.code, historyId]);

    /**
     * Loads purchase history shell data.
     */
    const loadHistory = useCallback(async () => {
        setLoading(true);
        await Utils.wait(300);
        if (!isMounted()) {
            return;
        }
        setDocument(buildMockPurchaseHistory({
            historyId,
            vendorId,
            vendorName: defaultVendor?.name,
            vendorCode: defaultVendor?.code,
        }));
        setLoading(false);
    }, [defaultVendor?.code, defaultVendor?.name, historyId, isMounted, vendorId]);

    useEffect(() => {
        void loadHistory();
    }, [loadHistory]);

    /**
     * Syncs the active tab into the URL query string.
     */
    const onTabChange = useCallback((_, value) => {
        setQuery({tab: value}, {replace: true});
    }, [setQuery]);

    const documentInfoSummary = useMemo(() => ({
        header: {
            title: t('summary.historyNo'),
            value: document?.historyNo ?? historyId,
            loadingValue: t('loading'),
            copyable: true,
        },
        body: [
            {
                id: documentBodyIds.vendor,
                title: t('summary.vendor'),
                value: document?.vendorName ?? '--',
            },
            {
                id: documentBodyIds.vendorPO,
                title: t('summary.vendorPO'),
                value: document?.vendorPO ?? '--',
            },
            {
                id: documentBodyIds.reference,
                title: t('summary.reference'),
                value: document?.reference ?? '--',
            },
            {
                id: documentBodyIds.date,
                title: t('summary.date'),
                value: formatSummaryDate(document?.date),
            },
            {
                id: documentBodyIds.receivedDate,
                title: t('summary.receivedDate'),
                value: formatSummaryDate(document?.receivedDate),
            },
            {
                id: documentBodyIds.warehouse,
                title: t('summary.warehouse'),
                value: document?.warehouse ?? '--',
            },
            {
                id: documentBodyIds.currency,
                title: t('summary.currency'),
                value: document?.currency ?? '--',
            },
            {
                id: documentBodyIds.status,
                title: t('status.label'),
                value: document?.status ?? '--',
            },
        ],
    }), [document, historyId, t]);

    const billInfoSummary = useMemo(() => ({
        header: {
            title: t('totals.title'),
            value: document ? formatMoney(document.total) : '--',
            loadingValue: t('loading'),
        },
        body: [
            {
                id: totalsBodyIds.subtotal,
                title: t('totals.subtotal'),
                value: document ? formatMoney(document.subtotal) : '--',
            },
            {
                id: totalsBodyIds.discount,
                title: t('totals.discount'),
                value: document ? formatMoney(document.discount) : '--',
            },
            {
                id: totalsBodyIds.tax,
                title: t('totals.tax'),
                value: document ? formatMoney(document.tax) : '--',
            },
            {
                id: totalsBodyIds.total,
                title: t('totals.total'),
                value: document ? formatMoney(document.total) : '--',
            },
            {
                id: totalsBodyIds.balance,
                title: t('totals.balance'),
                value: document ? formatMoney(document.balance) : '--',
            },
        ],
    }), [document, t]);

    return (
        <div className={'purchase-history-single-view w-100'}>
            <Row className={'no-gutters w-100 align-items-center justify-content-between'}>
                <Row className={'no-gutters align-items-center'}>
                    <IconButton
                        component={Link}
                        to={routeFunctions.vendor.purchases.history.base(vendorId)}
                        replace
                        color={'paper.secondary'}
                        aria-label={t('actions.back')}
                    >
                        <ArrowBack className={'icon normal'}/>
                    </IconButton>
                    <p className={'text-lg font-weight-semi-bold ml-2'}>
                        {
                            loading
                                ? t('titleLoading')
                                : t('title', {historyNo: document?.historyNo ?? historyId})
                        }
                    </p>
                </Row>
                {
                    operatorAccessLevels.packages.bizPrint.printPurchaseHistory &&
                    <Tooltip arrow title={t('actions.print')}>
                        <IconButton
                            color={'paper.secondary'}
                            component={Link}
                            target={'_blank'}
                            to={routeFunctions.print.purchaseHistory(vendorId, historyId)}
                            aria-label={t('actions.print')}
                        >
                            <SvgIcon className={'icon large'} component={Printer}/>
                        </IconButton>
                    </Tooltip>
                }
            </Row>

            <div className={'w-100 my-3'}/>

            <div className={'d-flex flex-wrap w-100 justify-content-start'}>
                <Col xs={12} md={6} xl={4} className={'mb-3'}>
                    <SummaryInfoCard
                        header={documentInfoSummary.header}
                        body={documentInfoSummary.body}
                        loading={loading}
                        loadingCount={documentInfoSummary.body.length}
                    />
                </Col>
                <Col xs={12} md={6} xl={4} className={'mb-3'}>
                    <PurchaseVendorInfoCard
                        vendorId={vendorId}
                        loading={loading}
                    />
                </Col>
                <Col xs={12} md={6} xl={4} className={'mb-3'} style={{overflowY: 'hidden'}}>
                    <SummaryActionCard
                        className={'auto-height'}
                        header={billInfoSummary.header}
                        body={billInfoSummary.body}
                        actions={[]}
                        loading={loading}
                        loadingCount={billInfoSummary.body.length}
                    />
                </Col>
            </div>

            <div className={'w-100 my-2'}/>

            <div className={'w-100 flex-grow-1'}>
                <Tabs value={tab} onChange={onTabChange}>
                    {
                        tabsList.map((entry) => (
                            <Tab
                                key={entry.value}
                                label={entry.label}
                                value={entry.value}
                            />
                        ))
                    }
                </Tabs>
            </div>

            <div className={'my-3 w-100'}/>

            {
                tab === tabs.products &&
                <PurchaseHistorySingleProductsSection
                    items={document?.items ?? []}
                    loading={loading}
                />
            }
            {
                tab === tabs.addresses &&
                <OrderInformationViewReadOnlyAddressesSection
                    billing={document?.billingAddress}
                    shipping={document?.shippingAddress}
                    loading={loading}
                />
            }
            {
                tab === tabs.logs &&
                <PurchaseHistorySingleLogsSection
                    logs={document?.logs ?? []}
                    loading={loading}
                />
            }

            <div className={'my-3 w-100'}/>
        </div>
    );
};

export default PurchaseHistorySingleView;
